import React from "react";
import { format } from "date-fns";
import CustomerInfo from "../../../../components/JewelleryPlans/common/CustomerInfo";
import {
  PLAN_TYPES,
  BENEFIT_TYPES,
} from "../../../../constants/jewellery.constant";

const PlanSummary = ({ plan }) => {
  const getPlanTypeLabel = (type) => {
    switch (type) {
      case PLAN_TYPES.VALUE_SAVINGS:
        return "Value Savings Plan";
      case PLAN_TYPES.WEIGHT_SAVINGS:
        return "Weight Savings Plan";
      case PLAN_TYPES.PRICE_LOCK:
        return "Price Lock Plan";
      default:
        return type;
    }
  };

  const getBenefitDescription = () => {
    const template = plan.templateSnapshot;
    if (!template) return null;

    switch (template.benefitType) {
      case BENEFIT_TYPES.FREE_INSTALLMENT:
        return `Free installment benefit: Get ${
          template.benefitParams?.freeInstallments || 1
        } free month after ${
          template.benefitParams?.triggerAfterN || 10
        } payments`;
      case BENEFIT_TYPES.VA_DISCOUNT_CAP:
        return `VA discount benefit: Maximum ${
          template.benefitParams?.maxPercent || 0
        }% VA on redemption`;
      case BENEFIT_TYPES.MAKING_CHARGE_WAIVER:
        return `Making charge benefit: ${
          template.benefitParams?.waiveChargesPercent || 0
        }% off on making charges`;
      default:
        return null;
    }
  };

  const getProgressPercentage = () => {
    if (!plan.templateSnapshot?.tenureMonths) return 0;
    return Math.min(
      (plan.paymentsDone / plan.templateSnapshot.tenureMonths) * 100,
      100
    );
  };

  return (
    <div className="space-y-6">
      {/* Customer Information */}
      <div>
        <h3 className="text-lg font-medium text-text mb-4">
          Customer Information
        </h3>
        <CustomerInfo customer={plan.userId} />
      </div>

      {/* Plan Details */}
      <div className="bg-background border border-neutral-light rounded-lg p-6">
        <h3 className="text-lg font-medium text-text mb-4">Plan Details</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-3">
            <div>
              <p className="text-sm text-neutral-dark">Plan Type</p>
              <p className="font-medium">
                {getPlanTypeLabel(plan.templateSnapshot?.type)}
              </p>
            </div>

            <div>
              <p className="text-sm text-neutral-dark">Template</p>
              <p className="font-medium">
                {plan.templateSnapshot?.name} ({plan.templateSnapshot?.code})
              </p>
            </div>

            <div>
              <p className="text-sm text-neutral-dark">Monthly Installment</p>
              <p className="font-medium">
                ₹{plan.monthlyAmount?.toLocaleString()}
              </p>
            </div>

            <div>
              <p className="text-sm text-neutral-dark">Tenure</p>
              <p className="font-medium">
                {plan.templateSnapshot?.tenureMonths} months
              </p>
            </div>
          </div>

          <div className="space-y-3">
            <div>
              <p className="text-sm text-neutral-dark">Start Date</p>
              <p className="font-medium">
                {format(new Date(plan.startDate), "dd MMM yyyy")}
              </p>
            </div>

            <div>
              <p className="text-sm text-neutral-dark">Maturity Date</p>
              <p className="font-medium">
                {plan.maturesOn
                  ? format(new Date(plan.maturesOn), "dd MMM yyyy")
                  : "Not set"}
              </p>
            </div>

            <div>
              <p className="text-sm text-neutral-dark">Created On</p>
              <p className="font-medium">
                {format(new Date(plan.createdAt), "dd MMM yyyy")}
              </p>
            </div>

            {plan.branchId && (
              <div>
                <p className="text-sm text-neutral-dark">Branch</p>
                <p className="font-medium">
                  {plan.branchId.name} ({plan.branchId.code})
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Benefits */}
        {getBenefitDescription() && (
          <div className="mt-4 p-3 bg-primary-light rounded-lg">
            <p className="text-sm text-white">
              ✨ {getBenefitDescription()}
            </p>
          </div>
        )}
      </div>

      {/* Progress & Wallet */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Progress Card */}
        <div className="bg-background border border-neutral-light rounded-lg p-6">
          <h3 className="text-lg font-medium text-text mb-4">
            Payment Progress
          </h3>

          <div className="space-y-4">
            <div>
              <div className="flex justify-between text-sm mb-2">
                <span className="text-neutral-dark">Completed</span>
                <span className="font-medium">
                  {plan.paymentsDone || 0} /{" "}
                  {plan.templateSnapshot?.tenureMonths || 0} payments
                </span>
              </div>
              <div className="w-full bg-neutral-light rounded-full h-3">
                <div
                  className="bg-success h-3 rounded-full transition-all duration-300"
                  style={{ width: `${getProgressPercentage()}%` }}
                />
              </div>
              <p className="text-xs text-neutral-dark mt-1">
                {getProgressPercentage().toFixed(1)}% complete
              </p>
            </div>

            <div className="pt-3 border-t border-neutral-light space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-neutral-dark">Total Paid</span>
                <span className="font-medium">
                  ₹{(plan.totalPaidAmount || 0).toLocaleString()}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-neutral-dark">Total Verified</span>
                <span className="font-medium">
                  ₹{(plan.totalVerifiedAmount || 0).toLocaleString()}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Wallet Card */}
        <div className="bg-background border border-neutral-light rounded-lg p-6">
          <h3 className="text-lg font-medium text-text mb-4">Wallet Balance</h3>

          <div className="space-y-4">
            {plan.templateSnapshot?.type === PLAN_TYPES.WEIGHT_SAVINGS ? (
              <>
                <div>
                  <p className="text-sm text-neutral-dark">Gold Accumulated</p>
                  <p className="text-2xl font-bold text-primary">
                    {plan.wallet?.grams || 0} grams
                  </p>
                </div>
                <div className="pt-3 border-t border-neutral-light">
                  <p className="text-sm text-neutral-dark">Average Rate</p>
                  <p className="font-medium">
                    ₹{(plan.averageAccrualRate || 0).toFixed(2)}/gram
                  </p>
                </div>
              </>
            ) : (
              <>
                <div>
                  <p className="text-sm text-neutral-dark">Value Accumulated</p>
                  <p className="text-2xl font-bold text-success">
                    ₹{(plan.wallet?.valueInr || 0).toLocaleString()}
                  </p>
                </div>
              </>
            )}

            {plan.benefitsEarned && (
              <div className="pt-3 border-t border-neutral-light">
                <p className="text-sm text-neutral-dark">Benefits Earned</p>
                <p className="font-medium">
                  ₹{(plan.benefitsEarned.totalAmount || 0).toLocaleString()}
                </p>
                {plan.benefitsEarned.freeInstallments > 0 && (
                  <p className="text-xs text-neutral-dark">
                    ({plan.benefitsEarned.freeInstallments} free installments)
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Notes */}
      {plan.notes && (
        <div className="bg-neutral-light rounded-lg p-4">
          <h4 className="text-sm font-medium text-text mb-2">Notes</h4>
          <p className="text-sm text-text">{plan.notes}</p>
        </div>
      )}
    </div>
  );
};

export default PlanSummary;
